import { FilterOption, FilterSelect } from "@components";
import { Feature, ListDataCategory, Role } from "@enums";
import {
  Add,
  Clear,
  FilterList,
  KeyboardDoubleArrowRight,
  LocationCity,
  Person,
  Search,
} from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ListDataOptionSerialisedOut,
  useProtocolApiV1ListDataQuery,
  UserOptionOut,
  useWebApiV1CreateOrderMutation,
  useWebApiV1GetTenantPhysiciansQuery,
  useWebApiV1GetUserFeatureSwitchesQuery,
} from "@providers/hop-ord-server/api";
import { useCheckUserRole } from "@utils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
  searchText: string;
  setSearchText: (searchText: string) => void;
  selectedStatus: string[];
  setSelectedStatus: (selectedStatus: string[]) => void;
  selectedSite: string[];
  setSelectedSite: (selectedSite: string[]) => void;
  selectedPhysician: string[];
  setSelectedPhysician: (selectedPhysician: string[]) => void;
  selectedQuickFilters: string[];
  setSelectedQuickFilters: (selectedQuickFilters: string[]) => void;
  tenantIds: number[];
}

type SiteOption = {
  value: string;
  label: string;
  tenantId: number;
};

const sortByName = (a: string, b: string) => a.localeCompare(b);

const STATUS_OPTIONS: FilterOption[] = [
  { label: "Draft", value: "Draft careplans" },
  { label: "New careplans", value: "New careplans" },
  { label: "Ready for planning", value: "Ready for planning" },
  { label: "Planning (Automation)", value: "Planning (Automation)" },
  { label: "Pending plan review", value: "Pending plan review" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
  { label: "Handover complete", value: "Handover complete" },
];

const QUICK_FITLERS: { label: string; value: string; tooltip?: string }[] = [
  { label: "Unread comments", value: "Unread comments" },
  { label: "Prescription changes", value: "Prescription changes" },
  { label: "Test view", value: "Test view", tooltip: "Show test cases only" },
];

const SearchAndFilterBar = ({
  searchText,
  setSearchText,
  selectedStatus,
  setSelectedStatus,
  selectedSite,
  setSelectedSite,
  selectedPhysician,
  setSelectedPhysician,
  selectedQuickFilters,
  setSelectedQuickFilters,
  tenantIds,
}: Props) => {
  const [addOrder] = useWebApiV1CreateOrderMutation();
  const navigate = useNavigate();
  const { data: treatingDepartments } = useProtocolApiV1ListDataQuery({
    categories: [ListDataCategory.TREATING_DEPARTMENT],
    tenantIds: tenantIds ?? [],
  });
  const { data: physicians } = useWebApiV1GetTenantPhysiciansQuery({});
  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // If there is only one clinic, automatically select it
  const [selectedClinic, setSelectedClinic] = useState<SiteOption | undefined>(
    undefined,
  );
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [clinicError, setClinicError] = useState<string>("");

  useEffect(() => {
    if (treatingDepartments?.length === 1) {
      setSelectedClinic({
        value: treatingDepartments[0].value,
        label: treatingDepartments[0].value,
        tenantId: treatingDepartments[0].tenantId,
      });
    }
  }, [treatingDepartments]);

  const isRo = useCheckUserRole(Role.RO);
  const showCreateOrderButton =
    featureSwitchData?.find(
      (feature) => feature.name === Feature.COMMAND_CENTER_CREATE_ORDER,
    ) !== undefined && !isRo;

  const createOrder = async (tenantId: number) => {
    setIsCreatingOrder(true);
    if (!selectedClinic) {
      setClinicError("This field is required");
      return;
    }
    try {
      const order = await addOrder({
        tenantId,
        treatingDepartment: selectedClinic.value,
      }).unwrap();
      navigate(`/order/${order}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleCreateOrderClick = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    if (treatingDepartments?.length !== 1) {
      setSelectedClinic(undefined);
    }
    setClinicError("");
  };

  const handleDialogContinue = async () => {
    if (!selectedClinic) {
      setClinicError("This field is required");
      return;
    }

    const tenantId = selectedClinic.tenantId;

    await createOrder(tenantId);
    handleDialogClose();
  };

  const siteOptions =
    treatingDepartments
      ?.map((listData: ListDataOptionSerialisedOut): SiteOption => {
        // If a value appears twice in the list, we need to show the tenant name as well.
        // Only do this for items that are duplicated at least twice.
        if (
          treatingDepartments?.filter(
            (option) => option?.value === listData?.value,
          ).length >= 2
        ) {
          const tenantName = listData?.tenantName.split(":").at(-1);
          return {
            value: listData?.value,
            label: listData?.value + " (" + tenantName + ")",
            tenantId: listData?.tenantId,
          };
        }
        return {
          value: listData?.value,
          label: listData?.value,
          tenantId: listData?.tenantId,
        };
      })
      .sort((a, b) => sortByName(a.label, b.label)) || [];

  const physicianOptions =
    physicians
      ?.map((physician: UserOptionOut) => ({
        label: physician?.name,
        value: String(physician?.id),
      }))
      .sort((a, b) => sortByName(a.label, b.label)) || [];

  const clearAllFilters = () => {
    setSelectedStatus([]);
    setSelectedSite([]);
    setSelectedPhysician([]);
    setSelectedQuickFilters([]);
  };

  const anyFilterSelected =
    selectedStatus.length > 0 ||
    selectedSite.length > 0 ||
    selectedPhysician.length > 0 ||
    selectedQuickFilters.length > 0;

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1}>
        {showCreateOrderButton && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            sx={{ marginRight: 2 }}
            onClick={handleCreateOrderClick}
          >
            Create Careplan
          </Button>
        )}
        <TextField
          id="search-text"
          placeholder="Search patient name or ID"
          variant="standard"
          value={searchText}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(event.target.value);
          }}
          sx={{
            width: "250px",
            marginRight: 2,
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FilterSelect
          label="Planning status"
          options={STATUS_OPTIONS}
          selectedOptions={selectedStatus}
          setSelectedOptions={setSelectedStatus}
          startIcon={<KeyboardDoubleArrowRight />}
        />
        <FilterSelect
          label="Treating clinic"
          options={siteOptions}
          selectedOptions={selectedSite}
          setSelectedOptions={setSelectedSite}
          startIcon={<LocationCity />}
        />
        <FilterSelect
          label="Physician"
          options={physicianOptions}
          selectedOptions={selectedPhysician}
          setSelectedOptions={setSelectedPhysician}
          startIcon={<Person />}
        />
        <FilterSelect
          label="Quick Filters"
          options={QUICK_FITLERS}
          selectedOptions={selectedQuickFilters}
          setSelectedOptions={setSelectedQuickFilters}
          startIcon={<FilterList />}
        />
        {anyFilterSelected && (
          <Button
            disabled={!anyFilterSelected}
            startIcon={<Clear />}
            onClick={clearAllFilters}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Careplan</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Select the treating clinic you are creating for.{" "}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }} error={!!clinicError}>
            <Autocomplete
              id="clinic-select"
              disableClearable
              options={siteOptions}
              value={selectedClinic}
              onChange={(_, newValue) => {
                setSelectedClinic(newValue || null);
                setClinicError("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Treating clinic*"
                  error={!!clinicError}
                  helperText={clinicError}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              getOptionLabel={(option) => option.label}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ width: 80 }}
            onClick={handleDialogClose}
            disabled={isCreatingOrder}
          >
            Cancel
          </Button>
          <Button
            sx={{ width: 80 }}
            onClick={handleDialogContinue}
            disabled={isCreatingOrder}
          >
            {!isCreatingOrder ? "Continue" : <CircularProgress size={24} />}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SearchAndFilterBar;
